// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Sign-in (design doc §7.1, §12.1).
 *
 * There is no form here and there never will be: this app holds no passwords. The button
 * is a plain link to the server's `/auth/login`, which mints the login transaction cookie
 * and redirects to Google. Doing it with a link rather than `fetch` matters — the flow is
 * a browser redirect chain, and an XHR would strip it of the top-level navigation the
 * provider requires.
 *
 * `return_to` is passed through so an operator who deep-linked to an archive and found an
 * expired session lands back on that archive, not on the workbench.
 *
 * A failed sign-in lands back here with `?error=<code>` (the server never shows the
 * browser a JSON error page): the code is explained in words, and the button becomes
 * "sign in again" — a fresh attempt, since the failed one's transaction is spent.
 */

const LOGIN_ERRORS = [
  "DOMAIN_NOT_ALLOWED",
  "EMAIL_NOT_VERIFIED",
  "ACCOUNT_DISABLED",
  "LOGIN_STATE_INVALID",
  "LOGIN_FAILED",
];

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";

export function Login() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const returnTo = params.get("return_to") ?? "/";
  const error = params.get("error");

  return (
    <div className="login">
      <BrandMark size={44} />
      <h1>{t("app.title")}</h1>
      {error && (
        <div className="banner banner-error" role="alert">
          {LOGIN_ERRORS.includes(error)
            ? t(`login.errors.${error}`)
            : t("login.errors.unknown", { code: error })}
        </div>
      )}
      <p className="muted">{t("login.hint")}</p>
      <a
        className="link-button"
        href={`/auth/login?return_to=${encodeURIComponent(returnTo)}`}
        data-testid="sso-login"
      >
        {error ? t("login.retry") : t("login.sso")}
      </a>
    </div>
  );
}
